import React, { ReactNode, FC, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

interface SignatureBoxOverlayProps {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
  signed: boolean;
  signedPreviewSvg?: { viewBox: string; paths: string[] } | null;
  signedText?: { text: string; fontFamily: string } | null;
  onClick?: () => void;
  isInitials?: boolean;
}

const SignatureBoxOverlay: FC<SignatureBoxOverlayProps> = ({
  x,
  y,
  width,
  height,
  scale,
  signed,
  signedPreviewSvg,
  signedText,
  onClick,
  isInitials = false,
}) => {
  const theme = useTheme();
  const signatureColor =
    theme.palette.signature?.main || theme.palette.warning.main;
  const successColor = theme.palette.success.main;

  const style = useMemo(
    () => ({
      position: 'absolute',
      left: x * scale,
      top: y * scale,
      width: width * scale,
      height: height * scale,
      cursor: signed ? 'default' : onClick ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      boxSizing: 'border-box',
      ...(signed
        ? {
            border: `1px solid ${alpha(successColor, 0.5)}`,
            backgroundColor: alpha(successColor, 0.06),
          }
        : {
            backgroundColor: alpha(signatureColor, 0.75),
            border: `1px solid ${signatureColor}`,
            '&:hover': {
              backgroundColor: alpha(signatureColor, 0.85),
            },
          }),
    }),
    [x, y, width, height, scale, signed, signatureColor, successColor, onClick]
  );

  const renderContent = (): ReactNode => {
    if (signed && signedPreviewSvg) {
      return (
        <svg
          viewBox={signedPreviewSvg.viewBox}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ padding: 2 }}
        >
          {signedPreviewSvg.paths.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="#000" strokeWidth={1.5} />
          ))}
        </svg>
      );
    }

    if (signed && signedText) {
      return (
        <Typography
          sx={{
            fontFamily: signedText.fontFamily,
            fontSize: Math.min(height * scale * 0.6, 24),
            color: '#000',
            lineHeight: 1,
          }}
        >
          {signedText.text}
        </Typography>
      );
    }

    return (
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 'bold',
          fontSize: Math.min(height * scale * 0.45, 14),
          lineHeight: 1,
        }}
      >
        {isInitials ? 'Initials Here' : 'Signature Here'}
      </Typography>
    );
  };

  return (
    <Box
      sx={style}
      role={!signed && onClick ? 'button' : undefined}
      tabIndex={!signed && onClick ? 0 : undefined}
      aria-label={
        signed
          ? 'Signature completed'
          : onClick
          ? isInitials
            ? 'Initials here'
            : 'Signature here'
          : undefined
      }
      onClick={() => {
        if (!signed && onClick) {
          onClick();
        }
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!signed && onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default SignatureBoxOverlay;
